import { dissoc } from 'ramda';
import { getCompany, getSelectedCompanyId } from '@twnel/companies-login';
import { getTexts } from '@twnel/web-components';
import { isNotEmpty, toastDispacher } from 'utils';
import logger from 'utils/logger';
import {
  SUCCESS_TYPE, ERROR_TYPE, BOT_DELETION,
  BOT_ERROR_GROUPS_GETTER, BOT_ERRORS_DETAILS_GETTER,
  BOT_CREATION, BOT_PUBLISHED, BOT_PUBLISHED_UPDATE,
} from 'utils/toastDispacher';
import { botspecsActions } from '../reducer';
import {
  getAllBotspecs, getBotspecInfo, getFolders, getTemplates,
} from '../selectors';

const NETWORK_ERROR = 'TwnelNetwork';

// Error de red con nombre propio. Se crea un Error nuevo: copiar con spread
// perdía `message` y `stack`, que no son enumerables.
export const networkError = (error) => {
  if (!(error instanceof Error)) {
    return new Error(error);
  }
  return Object.assign(new Error(error.message), error, { name: NETWORK_ERROR });
};

// Acciones del slice de botspecs (data/reducer), exportadas con los nombres de siempre.
export const {
  resetChatbots, updateSelectedBotspec, updateSelectedFolder, updateSelectedVariableGroup,
  updateSelectedFunctionGroup, updateSelectedStorageGroup, resetSelections, updateParsingBotspec,
  updateBotspecs, updateFolders, updateLoadingBotspecs, updateChatbots, updateCurrentBUnits,
  loadingBotErrorGroups, updateBotErrorGroups, loadingBotErrorsDetails, updateBotErrorsDetails,
} = botspecsActions;

export const throwNetworkError = (error) => {
  throw networkError(error);
};

export const postBotspec = (botspec, actionNotifier) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  const bots = getAllBotspecs(getState());
  const oldChatbot = bots[botspec.id];
  const post = oldChatbot ? api.botspecs.update : api.botspecs.create;
  return post(botspec).then((updatedBotspec) => {
    if (actionNotifier) {
      toastDispacher({ ...actionNotifier, texts, type: SUCCESS_TYPE });
    }

    dispatch(updateBotspecs({ ...bots, [botspec.id]: { ...bots[botspec.id], ...updatedBotspec } }));
    if (actionNotifier === BOT_CREATION) {
      updateSelectedBotspec(botspec.id);
    }
  }).catch((error) => {
    toastDispacher({ ...actionNotifier, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  });
};

export const saveBotSpec = (botspec, actionNotifier) => (dispatch, getState) => {
  const { botspec: oldBotspec } = getBotspecInfo(getState());
  dispatch(postBotspec({ ...oldBotspec, ...botspec }, actionNotifier));
};

export const buildDefaultBot = () => ({
  entryPoint: 1,
  nodes: {
    1: {
      id: 1,
      data: {
        isEntryPoint: true,
        title: 'Send Message#1',
        properties: {
          title: 'Send Message#1',
          type: 'send_message',
          vars: {},
          messages: {},
          input: {},
          media: { mediaUrl: '' },
          closeChat: false,
          delay: 0,
          resultVariables: {},
        },
      },
      inputs: {
        rcv: {
          connections: [],
        },
      },
      outputs: {
        nxt: {
          connections: [],
        },
      },
      position: [248, 338],
      name: 'Send Message',
    },
  },
  data: {
    1: {
      properties: {
        title: 'Send Message#1',
        type: 'send_message',
        vars: {},
        messages: {},
        input: {},
        mediaUrl: '',
        metadata: { media: '' },
        closeChat: false,
        delay: 0,
        resultVariables: {},
      },
      connections: [],
      isEntryPoint: true,
    },
  },
});

export const createBotSpec = ({
  id,
  name,
  description,
  type = 'json',
  selectedFolder,
  specJson,
  actionNotifier,
}) => (dispatch, getState) => {
  const company = getCompany(getSelectedCompanyId(getState()))(getState());
  const chatbot = {
    id,
    name,
    description,
    company: company.id,
    country: company.country,
    rete_json: {
      id: `${id}@1.0.0`,
      name,
      entryPoint: 1,
      nodes: {},
      data: {},
      variables: {},
      ...type === 'visual' && buildDefaultBot(),
    },
    spec_json: specJson || {
      version: '1.0',
      variables: {
        user: {
          phone: { type: 'string', value: '' },
          country: { type: 'string', value: '' },
          name: { type: 'string', value: '' },
          tags: { type: 'array', value: [] },
          session_id: { type: 'string', value: '' },
        },
        chatbot: {},
        session: {},
      },
      files: {},
      functions: {},
      rules: {},
      messages: {},
      storage: {
        customer: {
          enable: false,
          drivers: {},
        },
        twnel: {
          enable: true,
          mode: 'secure',
        },
      },
      transitions: {},
      actions: {},
    },
    last_updated: Date.now(),
    enable: true,
    type,
    folder: selectedFolder,
  };

  return dispatch(postBotspec(chatbot, actionNotifier))
    .then(() => dispatch(updateSelectedBotspec(id)));
};

export const deleteBotspec = (id) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const botspecs = getAllBotspecs(getState());
  const texts = getTexts(getState());
  const updatedBotspecs = dissoc(id, botspecs);
  return api.botspecs.delete(id).then(() => {
    toastDispacher({
      actionId: BOT_DELETION,
      texts,
      type: SUCCESS_TYPE,
    });
    dispatch(updateBotspecs(updatedBotspecs));
  }).catch(() => {
    toastDispacher({
      actionId: BOT_DELETION,
      texts,
      type: ERROR_TYPE,
    });
  });
};

export const loadBotspecsIfNeeded = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const stateBotspecs = getAllBotspecs(getState());
  if (!isNotEmpty(stateBotspecs)) {
    return api.botspecs.getAll().then((botspecs) => {
      const reduceChatbots = botspecs.reduce((accum, chatbot) => (
        { ...accum, [chatbot.id]: { ...chatbot } }
      ), {});
      dispatch(updateLoadingBotspecs(false));
      dispatch(updateBotspecs(reduceChatbots));
    });
  }
};

export const loadFoldersIfNeeded = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const folders = getFolders(getState());
  if (!isNotEmpty(folders)) {
    return api.chatbots.getFolders().then((data) => {
      const reduceChatbots = data.reduce((accum, folder) => (
        { ...accum, [folder.id]: { ...folder } }
      ), {});
      dispatch(updateFolders(reduceChatbots));
    });
  }
};

export const storeInContext = (key, value) => (dispatch, getState, getContext) => {
  const { updateContext } = getContext();
  updateContext({ [key]: value });
};

export const resetBotspecs = () => (dispatch) => {
  dispatch(updateLoadingBotspecs(true));
  dispatch(updateBotspecs({}));
  dispatch(loadBotspecsIfNeeded());
};

export const resetFolders = () => (dispatch) => {
  dispatch(updateFolders({}));
  dispatch(loadFoldersIfNeeded());
};

export const makeApiCall = (apiRequest) => {
  const {
    url,
    method,
    params,
    headers,
    body,
  } = apiRequest;

  let queryParams = '';
  /** @type {RequestInit} */
  let config = null;
  if (method === 'get') {
    queryParams = `?${Object.keys(params).filter((key) => params[key] !== null).map((key) => (
      `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )).join('&')}`;

    config = {
      method,
      headers,
      mode: 'cors',
    };
  } else if (method === 'delete') {
    config = {
      method,
      headers,
      mode: 'cors',
    };
  } else {
    config = {
      method,
      headers,
      body,
      mode: 'cors',
    };
  }

  const finalUrl = `${url}${queryParams}`;

  return fetch(finalUrl, config);
};

export const getErrorGroups = ({ id, start, end }) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const reduxState = getState();
  const texts = getTexts(reduxState);
  dispatch(loadingBotErrorGroups(id, true));
  return api.chatbots.getErrorGroups({ id, start, end }).then((errorsGroups) => {
    const errorsInfo = errorsGroups;
    delete errorsInfo.query;
    dispatch(updateBotErrorGroups(id, errorsInfo));
  }).catch((error) => {
    dispatch(loadingBotErrorGroups(id, false));
    toastDispacher({ actionId: BOT_ERROR_GROUPS_GETTER, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  });
};

export const getErrorDetails = ({
  id, start, end, gid,
}) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const reduxState = getState();
  const texts = getTexts(reduxState);
  dispatch(loadingBotErrorsDetails(id, true));
  return api.chatbots.getErrorDetails({
    id, start, end, gid,
  }).then((errorsDetails) => {
    const errorsInfo = errorsDetails;
    delete errorsInfo.query;
    dispatch(updateBotErrorsDetails(id, gid, errorsInfo));
  }).catch((error) => {
    dispatch(loadingBotErrorsDetails(id, false));
    toastDispacher({ actionId: BOT_ERRORS_DETAILS_GETTER, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  });
};

export const restoreBotspecPastState = () => (dispatch, getState) => {
  const { botspec } = getBotspecInfo(getState());
  if (botspec) {
    dispatch(botspecsActions.restoreBotspecPastState(botspec.id));
  }
};

export const duplicateBotspec = (botspecId, name, description, duplicateId) => async (
  dispatch, getState,
) => {
  const botspecs = getAllBotspecs(getState());
  const botspec = botspecs[botspecId];

  if (botspec.rete_json.template) {
    delete botspec.rete_json.template;
  }

  const duplicatedBotspec = {
    ...botspec,
    id: duplicateId,
    name,
    description,
    rete_json: {},
    spec_json: {},
  };

  const updateBotSpec = {
    ...duplicatedBotspec,
    rete_json: { ...botspec.rete_json, id: `${duplicateId}@1.0.0`, name },
    spec_json: botspec.spec_json,
  };
  // Los errores de postBotspec ya son errores de red: se propagan sin volver a
  // envolverlos (antes quedaban como `Error('[object Object]')`).
  await dispatch(postBotspec(duplicatedBotspec, {
    actionId: BOT_CREATION,
    elementName: name,
  }));
  await dispatch(postBotspec(updateBotSpec, {
    actionId: BOT_CREATION,
    elementName: name,
  }));
  dispatch(updateSelectedBotspec(duplicateId));
  return botspec.type;
};

export const createTemplate = (templateId, name, id) => async (
  dispatch, getState, getContext,
) => {
  const company = getCompany(getSelectedCompanyId(getState()))(getState());
  const templates = getTemplates(getState());
  let template = templates.find((t) => t.id === templateId);
  const bots = getAllBotspecs(getState());
  const { api } = getContext();
  const { update, create } = api.botspecs;
  const texts = getTexts(getState());
  template = dissoc('_id', template);

  const duplicatedBotspec = {
    ...template,
    id,
    name,
    description: '',
    company: company.id,
    country: company.country,
    folder: '',
    rete_json: {},
    spec_json: {},
  };

  const updateBotspec = {
    ...duplicatedBotspec,
    rete_json: { ...template.rete_json, id: `${id}@1.0.0`, name },
    spec_json: template.spec_json,
  };

  try {
    await create(duplicatedBotspec);
    await update(updateBotspec);
    dispatch(
      updateBotspecs({
        ...bots,
        [updateBotspec.id]: { ...bots[updateBotspec.id], ...updateBotspec },
      }),
    );
    dispatch(updateSelectedBotspec(id));
  } catch (error) {
    toastDispacher({ actionId: BOT_CREATION, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  }
  return template.type;
};

export const saveBotspecFolderType = (botspecId, folderId) => (dispatch, getState) => {
  const botspecs = getAllBotspecs(getState());
  const botspec = botspecs[botspecId];

  return dispatch(postBotspec({ ...botspec, folder: folderId }));
};

export const createBotspecFolder = (name, parentId) => (dispatch, _, getContext) => {
  const { api } = getContext();

  return api.chatbots.createFolder({ name, parent: parentId })
    .then((folder) => {
      dispatch(botspecsActions.addFolder(folder));
    });
};

export const deleteBotspecFolder = (folderId) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const botspecs = getAllBotspecs(getState());
  const folders = getFolders(getState());

  return api.chatbots.deleteFolder({ id: folderId })
    .then(() => {
      const deletedFolder = folders[folderId];
      const classifiedBotspecs = Object.keys(botspecs).reduce((accum, key) => {
        const botspec = botspecs[key];
        if (botspec.folder === deletedFolder.id) {
          return {
            ...accum,
            [key]: { ...botspec, folder: deletedFolder.parent.length > 0 ? deletedFolder.parent : '' },
          };
        }
        return accum;
      }, botspecs);

      const { parent } = deletedFolder;
      const childs = Object.values(folders).filter((folder) => folder.parent === deletedFolder.id);

      const modifiedFolders = childs.reduce((accum, folder) => ({
        ...accum,
        [folder.id]: { ...folder, parent },
      }), folders);

      dispatch(updateBotspecs(classifiedBotspecs));
      dispatch(updateFolders(dissoc(folderId, modifiedFolders)));
    });
};

export const updateFolder = (folderId, parentId, name) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const folders = getFolders(getState());

  return api.chatbots.updateFolder(
    { id: folderId, ...name && { name }, parent: parentId },
  )
    .then(() => {
      dispatch(updateFolders(
        {
          ...folders,
          [folderId]: {
            ...folders[folderId],
            parent: parentId,
            ...name && { name },
          },
        },
      ));
    });
};

export const getChatbots = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  return api.chatbots.getAll()
    .then((chatbots) => dispatch(updateChatbots(chatbots)));
};

export const getCurrentBusinessUnits = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  return api.chatbots.getChatbotBUnits()
    .then((businessUnits) => dispatch(updateCurrentBUnits(businessUnits)));
};

// Después de publicar o editar la publicación, el editor necesita los chatbots y sus
// unidades de negocio actuales (B28). Si la recarga falla, la publicación igual se hizo.
const refreshPublishedChatbots = () => (dispatch) => Promise.all([
  dispatch(getChatbots()),
  dispatch(getCurrentBusinessUnits()),
]).catch((error) => logger.error('Error recargando los chatbots publicados:', error));

export const createChatbotBUnit = (chatbotId, businessUnitId) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  return api.chatbots.createChatbotBUnit({ chatbotId, businessUnitId });
};

export const createChatbot = (
  {
    name, answer, botspecId, country, description,
    enable, type, businessUnitsInfo,
  },
) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  return api.chatbots.create(
    {
      name, botspec_id: botspecId, country, description, enable, type, answer,
    },
  )
    .then((newChatbot) => {
      const businessUnitPromises = [];
      businessUnitsInfo.forEach((businessUnit) => {
        businessUnitPromises.push(dispatch(createChatbotBUnit(newChatbot.id, businessUnit.id)));
      });
      return Promise.all(businessUnitPromises);
    })
    .then(() => dispatch(refreshPublishedChatbots()))
    .then(() => {
      toastDispacher({
        actionId: BOT_PUBLISHED,
        texts,
        type: SUCCESS_TYPE,
      });
    })
    .catch((error) => {
      toastDispacher({ actionId: BOT_PUBLISHED, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

export const deleteChatbotBUnit = (chatbotId, businessUnitId) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  return api.chatbots.deleteChatbotBUnit({ chatbotId, businessUnitId });
};

export const updateChatbotPublished = (
  {
    id, name, answer, botspecId, description,
    enable, type, businessUnitsInfo,
    businessUnitsUpdate,
  },
) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  return api.chatbots.update({
    id, name, answer, description, type, enable, botspec_id: botspecId,
  })
    .then(() => {
      const businessUnitPromises = [];
      businessUnitsUpdate.forEach((businessUnit) => {
        businessUnitPromises.push(dispatch(deleteChatbotBUnit(id, businessUnit.id)));
      });
      return Promise.all(businessUnitPromises);
    })
    .then(() => {
      const businessUnitPromises = [];
      businessUnitsInfo.forEach((businessUnit) => {
        businessUnitPromises.push(dispatch(createChatbotBUnit(id, businessUnit.id)));
      });
      return Promise.all(businessUnitPromises);
    })
    .then(() => dispatch(refreshPublishedChatbots()))
    .then(() => {
      toastDispacher({
        actionId: BOT_PUBLISHED_UPDATE,
        texts,
        type: SUCCESS_TYPE,
      });
    })
    .catch((error) => {
      toastDispacher({ actionId: BOT_PUBLISHED_UPDATE, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};
